import { Maybe } from "graphql/jsutils/Maybe";
import {
  ContractType,
  CostClassificationType,
  HomeownerContractChangeOrder,
  HomeownerContractLineItem,
  SaveInvoiceLineItemInput,
} from "src/generated/graphqlTypes";
import { ConfirmLineItemsHomeownerContractLineItemFragment } from "src/routes/projects/invoices/invoice-v2/invoice-steps/ConfirmLineItems.generated";
import { calcPercent, trimNumber } from "src/utils";
import { ObjectState, Rule } from "src/utils/formState";
import { InvoiceV2_HomeownerContractLineItemFragment } from "./invoice-v2/InvoiceCreatePage.generated";

const maxExternalInvoiceNumber = 125000;

export const invoiceNumberRule: Rule<Maybe<string>> = ({ value, originalValue }) => {
  const valueAsNum = Number(value);
  if (
    // If invoiceNumber is undefined or null consider it valid. This signals the graphql-service that we want a new invoice number generated
    value === undefined ||
    value === null ||
    // If the invoiceNumber is equal to its original value, then consider it valid.
    originalValue === value ||
    // If it is not a number then consider it valid, as we do not need to worry about collision with BP invoice numbers
    isNaN(valueAsNum) ||
    // If a new value has been entered, then that means the user is entering an external account number and that must fit between the defined bounds to be valid.
    (valueAsNum > 0 && valueAsNum < maxExternalInvoiceNumber)
  ) {
    return undefined;
  }
  // If it did not pass the above validation, then the user entered an external invoice number outside of the bounds.
  return "External Invoice Numbers cannot be a number greater than or equal to 125000";
};

export function ownerContractType(hcli: InvoiceV2_HomeownerContractLineItemFragment): ContractType {
  // Whenever change order contract type is BudgetReallocation, we need to get the contract type from the parent contract
  if (
    hcli.owner.__typename === "HomeownerContractChangeOrder" &&
    hcli.owner.contractType === ContractType.BudgetReallocation
  ) {
    return (hcli.owner as HomeownerContractChangeOrder).contract.contractType;
  }

  return hcli.owner.contractType;
}

export function isFixedMarkupByContractType(contractType: ContractType) {
  return [ContractType.Fixed, ContractType.BudgetReallocation].includes(contractType);
}

export function calculateHCLIMarkup(hcli: ConfirmLineItemsHomeownerContractLineItemFragment) {
  return isFixedMarkupByContractType(hcli.owner.contractType) ? 1 : 1 + hcli.owner.costPlusMarkupBasisPoints! / 10000;
}

/** Returns if Homeowner contract line item is a Cost+ contract and there is no cost */
export function isCostPlusWithoutCost(hcli: InvoiceV2_HomeownerContractLineItemFragment) {
  return (
    ownerContractType(hcli) === ContractType.CostPlus &&
    hcli.budgetChangeInCents === 0 &&
    (hcli.priceChangeInCents ?? 0) > 0
  );
}

export type PricePercentageType = { price: number; percentage: number };

export type ContractLineItem = SaveInvoiceLineItemInput &
  Pick<HomeownerContractLineItem, "priceChangeInCents"> & {
    name: string;
    isChangeOrder: boolean;
    percentComplete: Maybe<number>;
    minPercentage: number;
    /** `projectItemId` allows us to identify and group all rows related to the same ProjectItem (i.e. prime contract & change order rows) */
    projectItemId: string;
    otherInvoices: PricePercentageType | undefined;
    uninvoiced: PricePercentageType;
    billIds: { id: string }[];
    costClassification?: CostClassificationType;
    marginInCents?: number;
    downPaymentInvoiceId?: string | null;
  };

export function calculateUninvoiced(
  row: ObjectState<ContractLineItem>,
  amountInCents: number,
  percentage: number,
): PricePercentageType {
  const priceChangeInCents = row.priceChangeInCents.value || 0;
  const totalAmountInvoiced = amountInCents + (row.otherInvoices.value?.price || 0);
  const uninvoicedPrice = priceChangeInCents - totalAmountInvoiced;
  const uninvoicedPercentage = 100 - percentage;
  return { price: uninvoicedPrice, percentage: uninvoicedPercentage };
}

export function calculatePercentageCompleteByAmount(row: ObjectState<ContractLineItem>, amountInCents: number): number {
  const priceChangeInCents = row.priceChangeInCents.value || 0;
  let otherPrice = 0;
  let otherPercentage = 0;
  if (row.otherInvoices.value) {
    otherPrice = row.otherInvoices.value.price;
    otherPercentage = row.otherInvoices.value.percentage;
  }
  const totalAmountInvoiced = amountInCents + otherPrice;

  return !amountInCents
    ? // reset % Complete to be equal to previously invoiced percentage
      otherPercentage
    : trimNumber(calcPercent(totalAmountInvoiced, priceChangeInCents));
}

export function calculateAmountByPercentage(row: ObjectState<ContractLineItem>, percentage: number): number {
  // Our `percentage` field is the sum of all invoice %'s. To properly calculate this invoice's amount we first need to subtract the the other invoice's percentage.
  const thisPercentage = percentage - (row.otherInvoices.value?.percentage || 0);
  // If either the percentage or `priceChangeInCents` are 0 or not defined, then return 0 for the amount.
  return !thisPercentage || !row.priceChangeInCents.value
    ? 0
    : Math.round(row.priceChangeInCents.value * (thisPercentage / 100));
}
